import { Alert } from '@/components/ui/alert';
import { EditablePreview, EditableRoot, EditableTextarea } from '@/components/ui/editable';
import { useUpdateBrandDnaAction } from '@/hooks/actions/use-update-brand-dna-action';
import { Box, Button, Flex, List, Text, VStack } from '@chakra-ui/react';
import { NavArrowDown, NavArrowUp } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const MIN_CHARS = 50;
const MAX_CHARS = 2000;
const COUNTER_THRESHOLD = 200;

interface BrandDnaWriteStepProps {
  initialText?: string;
  sourceUrl?: string | null;
  onCancel: () => void;
  onSaved: () => void;
}

const BrandDnaWriteStep = ({
  initialText = '',
  sourceUrl,
  onCancel,
  onSaved,
}: BrandDnaWriteStepProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState(initialText);
  const [showInspiration, setShowInspiration] = useState(false);
  const updateBrandDna = useUpdateBrandDnaAction();

  const charCount = text.length;
  const isTooShort = charCount > 0 && charCount < MIN_CHARS;
  const canSave = charCount >= MIN_CHARS && charCount <= MAX_CHARS;

  const handleSave = () => {
    if (!canSave) return;
    updateBrandDna.mutate(
      { dna: text, ...(sourceUrl ? { sourceUrl } : {}) },
      { onSuccess: onSaved }
    );
  };

  const questions = t('settings.brandDna.write.questions', {
    returnObjects: true,
  }) as string[];

  return (
    <VStack gap={5} align="stretch">
      {sourceUrl && <Alert status="neutral" title={t('settings.brandDna.scan.successBanner')} />}

      <Box>
        <Text fontWeight="medium" mb={2}>
          {t('settings.brandDna.write.title')}
        </Text>
        <EditableRoot
          value={text}
          onValueChange={(e) => setText(e.value.slice(0, MAX_CHARS))}
          activationMode="click"
          submitMode="blur"
          defaultEdit={!initialText}
          placeholder={{
            edit: t('settings.brandDna.write.placeholder'),
            preview: t('settings.brandDna.write.placeholder'),
          }}
          maxLength={MAX_CHARS}
          width="full"
          alignItems="stretch"
          textAlign="start"
        >
          <EditablePreview
            minH="160px"
            alignItems="flex-start"
            width="full"
            maxW="full"
            whiteSpace="pre-wrap"
            overflowWrap="anywhere"
            color={text ? 'fg' : 'fg.subtle'}
            fontSize="lg"
            lineHeight="tall"
            bg="transparent"
            border="none"
            borderRadius="0"
            px={0}
            cursor="text"
          />
          <EditableTextarea
            autoFocus
            minH="160px"
            width="full"
            maxW="full"
            textAlign="start"
            overflowWrap="anywhere"
            fontSize="lg"
            lineHeight="tall"
            bg="transparent"
            focusRing="none"
            border="none"
            borderRadius="0"
            px={0}
            resize="none"
          />
        </EditableRoot>
        <Flex justify="space-between" mt={1}>
          {isTooShort ? (
            <Text fontSize="xs" color="red.500">
              {t('settings.brandDna.write.minCharsWarning', { min: MIN_CHARS })}
            </Text>
          ) : (
            <Box />
          )}
          {charCount > COUNTER_THRESHOLD && (
            <Text fontSize="xs" color="fg.subtle">
              {t('settings.brandDna.write.charCount', {
                count: charCount,
                max: MAX_CHARS,
              })}
            </Text>
          )}
        </Flex>
      </Box>

      <Box>
        <Button
          variant="plain"
          size="sm"
          onClick={() => setShowInspiration(!showInspiration)}
          px={0}
          gap={1.5}
          color="fg.muted"
          _hover={{ color: 'fg' }}
        >
          {t('settings.brandDna.write.inspirationTitle')}
          {showInspiration ? (
            <NavArrowUp width={16} height={16} />
          ) : (
            <NavArrowDown width={16} height={16} />
          )}
        </Button>
        {showInspiration && Array.isArray(questions) && (
          <List.Root gap={1} mt={2} pl={4}>
            {questions.map((question, index) => (
              <List.Item key={index} fontSize="sm" color="fg.muted">
                {question}
              </List.Item>
            ))}
          </List.Root>
        )}
      </Box>

      <Flex justify="flex-end" gap={2}>
        <Button variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={!canSave} loading={updateBrandDna.isPending}>
          {t('common.save')}
        </Button>
      </Flex>
    </VStack>
  );
};

export default BrandDnaWriteStep;
